import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { Row, Col, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ButtonComponent } from "../../../components/elements";
import { FiSave, FiX, FiUser, FiMail, FiShield } from "react-icons/fi";
import Swal from 'sweetalert2';

export default function UserEdit() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [roles, setRoles] = useState([]);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        role: ""
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchUserData();
        fetchRoles();
    }, [id]);

    const fetchUserData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/users/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (res.data.success) {
                const user = res.data.user;
                setFormData({
                    name: user.name || "",
                    email: user.email || "",
                    role: user.roles && user.roles.length > 0 ? user.roles[0].id : ""
                });
            }
        } catch (err) {
            console.error("Error fetching user data:", err);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t("Failed to load user data"),
                showConfirmButton: false,
                timer: 2000
            });
            navigate("/users");
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const token = localStorage.getItem("authToken");
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/roles`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (res.data.success && res.data.roles && res.data.roles.data) {
                setRoles(res.data.roles.data);
            } else if (res.data.roles && Array.isArray(res.data.roles)) {
                setRoles(res.data.roles);
            } else {
                setRoles([]);
            }
        } catch (err) {
            console.error("Error fetching roles:", err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});

        try {
            const token = localStorage.getItem("authToken");
            const res = await axios.put(`${process.env.REACT_APP_API_URL}/users/${id}`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t("User updated successfully"),
                    showConfirmButton: false,
                    timer: 2000
                });
                navigate("/users");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: t("Please check the form for errors"),
                    showConfirmButton: false,
                    timer: 2000
                });
            } else {
                console.error("Error updating user:", err);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: err.response?.data?.message || t("Failed to update user"),
                    showConfirmButton: false,
                    timer: 2000
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">{t("Loading user data...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="mb-4">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h4>{t("Edit User")}</h4>
                                <ButtonComponent
                                    className="btn btn-secondary"
                                    onClick={() => navigate("/users")}
                                    text={t("Back to Users")}
                                />
                            </div>

                            <Form onSubmit={handleSubmit}>
                                <Row className="mb-4">
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FiUser className="me-1" /> {t("Full Name")} <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder={t("Enter full name")}
                                                isInvalid={!!errors.name}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.name}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FiMail className="me-1" /> {t("Email Address")} <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder={t("Enter email address")}
                                                isInvalid={!!errors.email}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.email}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-4">
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FiShield className="me-1" /> {t("Role")} <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="role"
                                                value={formData.role}
                                                onChange={handleChange}
                                                isInvalid={!!errors.role}
                                            >
                                                <option value="">{t("Select a role")}</option>
                                                {roles.map(role => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.role}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="alert alert-info">
                                    <small>
                                        <FiShield className="me-1" />
                                        {t("Note: Password cannot be changed here. Use the password reset feature if needed.")}
                                    </small>
                                </div>

                                <div className="d-flex gap-2">
                                    <ButtonComponent
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={submitting}
                                        text={
                                            submitting ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    {t("Updating...")}
                                                </>
                                            ) : (
                                                <>
                                                    <FiSave className="me-1" /> {t("Update User")}
                                                </>
                                            )
                                        }
                                    />
                                    <ButtonComponent
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => navigate("/users")}
                                        text={
                                            <>
                                                <FiX className="me-1" /> {t("Cancel")}
                                            </>
                                        }
                                    />
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}